// src/Component/Login/AzurePreAuthPage.jsx
import React, { useState } from "react";
import {
  FormGroup,
  FormControl,
  ControlLabel,
  HelpBlock,
  Button,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { Auth } from "aws-amplify";
import DocumentSync from "../../Component/Images/DocumentSync-Logo.png";
import "./login.css";

import { userExists, getUserClientByEmail, getClientIdp } from "../../libs/API";

function getErrMsg(err) {
  if (err && err.response) {
    return (
      err.response.data || err.response.statusText || "Something went wrong."
    );
  }
  return err && err.message ? err.message : "Something went wrong.";
}

const norm = (v) => (v || "").trim();

export default function AzurePreAuthPage(props) {
  const [email, setEmail] = useState("");
  const [clientId, setClientId] = useState("");
  const [askClientId, setAskClientId] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  async function onNext(e) {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const eMail = norm(email);
    if (!eMail) return;

    setBusy(true);
    setMsg("");

    try {
      // 1) Does user exist in Membership?
      const existsResp = await userExists(eMail);
      const exists =
        existsResp && typeof existsResp.exists === "boolean"
          ? existsResp.exists
          : !!(existsResp && existsResp.exists);

      if (exists) {
        // 2) Resolve ClientId from aspnet_Users
        const clientResp = await getUserClientByEmail(eMail);
        const resolvedClientId =
          clientResp && clientResp.clientId != null
            ? clientResp.clientId
            : null;

        if (!resolvedClientId) {
          // Fallback to manual client id if lookup fails
          setAskClientId(true);
          return;
        }

        // 3) Get IdP by client
        const idpResp = await getClientIdp(resolvedClientId);
        const idpAlias = idpResp && idpResp.idpAlias ? idpResp.idpAlias : null;

        if (!idpAlias) {
          setMsg(
            "Your company hasn't been set up for Azure sign-in yet. Please contact Foveonics administration."
          );
          return;
        }

        // write a very short-lived fallback for the redirect round-trip
        localStorage.setItem("pendingClientId", String(resolvedClientId));
        sessionStorage.setItem("pendingClientId", String(resolvedClientId));
        launchHostedUIWithPrompt({
          idpAlias,
          email: eMail,
          clientId: resolvedClientId,
        });

        // await Auth.federatedSignIn({
        //   provider: idpAlias,
        //   customState: JSON.stringify({
        //     clientId: resolvedClientId,
        //     email: eMail,
        //   }),
        // });
        return; // browser leaves SPA
      }

      // New user → ask for Client ID
      setAskClientId(true);
    } catch (err) {
      setMsg(getErrMsg(err));
    } finally {
      setBusy(false);
    }
  }

  // Build a Hosted UI URL that forces the Azure account picker
  function launchHostedUIWithPrompt({ idpAlias, email, clientId }) {
    const cfg = Auth.configure();
    const oauth = cfg.oauth || {};
    const domain = oauth.domain; // e.g., foveonics-login.auth.us-east-1.amazoncognito.com
    const redirectUri = oauth.redirectSignIn;
    const clientIdCognito = cfg.userPoolWebClientId;
    const scope = Array.isArray(oauth.scope)
      ? oauth.scope.join(" ")
      : oauth.scope || "openid email profile";

    const state = JSON.stringify({ clientId, email });
    const qs = new URLSearchParams({
      identity_provider: idpAlias,
      redirect_uri: redirectUri,
      response_type: "code",
      client_id: clientIdCognito,
      scope,
      state,
      // force Azure to ignore existing SSO and show the picker
      prompt: "login",
      login_hint: email || "",
    });

    const url = `https://${domain}/oauth2/authorize?${qs.toString()}`;
    window.location.assign(url); // leave SPA
  }

  async function onContinue(e) {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const cid = norm(clientId);
    if (!cid) return;

    setBusy(true);
    setMsg("");

    try {
      const idpResp = await getClientIdp(cid);
      const idpAlias = idpResp && idpResp.idpAlias ? idpResp.idpAlias : null;

      if (!idpAlias) {
        setMsg(
          "Your company hasn't been set up for Azure sign-in yet. Please contact Foveonics administration."
        );
        return;
      }

      // stash fallback so the callback can recover even if ?state= is missing
      localStorage.setItem("pendingClientId", String(cid));
      sessionStorage.setItem("pendingClientId", String(cid));

      // await Auth.federatedSignIn({
      //   provider: idpAlias,
      //   customState: JSON.stringify({ clientId: cid, email: norm(email) }),
      // });

      launchHostedUIWithPrompt({
        idpAlias,
        email: norm(email),
        clientId: cid,
      });
      // no code after this — Hosted UI redirects away
    } catch (err) {
      setMsg(getErrMsg(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div id="loginmain">
      <div id="loginheader"></div>
      <div id="logincontent">
        <div id="logo">
          <Link to="/">
            <img src={DocumentSync} alt="DocumentSync" />
          </Link>
        </div>

        <br />
        <br />

        {!askClientId && (
          <form
            className="login-form"
            onSubmit={(evt) => {
              evt.preventDefault();
              onNext(evt);
            }}
          >
            <HelpBlock>
              Enter your email to continue with Azure sign-in.
            </HelpBlock>

            <FormGroup controlId="email">
              <ControlLabel>Email</ControlLabel>
              <FormControl
                type="email"
                value={email}
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormGroup>

            {msg ? <div className="alert alert-warning">{msg}</div> : null}

            <FormGroup>
              <Button
                type="submit"
                disabled={!email || busy}
                onClick={onNext}
                style={{ width: "100%" }}
                block
                bsClass="btn btn-outline-secondary"
                bsSize="large"
                aria-disabled={busy}
              >
                {busy ? "Checking..." : "Next"}
              </Button>
            </FormGroup>

            <div className="text-center" style={{ marginTop: 8 }}>
              <Link to="/" className="chat-link">
                Use username &amp; password instead
              </Link>
            </div>
          </form>
        )}

        {askClientId && (
          <form className="login-form" onSubmit={onContinue}>
            <HelpBlock>
              Your account isn’t in our system yet. Enter your <b>Client ID</b>{" "}
              to continue.
            </HelpBlock>

            <FormGroup controlId="clientId">
              <ControlLabel>Client ID</ControlLabel>
              <FormControl
                type="number"
                inputMode="numeric"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="Enter your Client ID"
              />
            </FormGroup>

            {msg ? <div className="alert alert-warning">{msg}</div> : null}

            <FormGroup>
              <Button
                type="submit"
                disabled={!clientId || busy}
                style={{ width: "100%" }}
                block
                bsClass="btn btn-outline-secondary"
                bsSize="large"
                aria-disabled={busy}
              >
                {busy ? "Checking..." : "Enable & Continue"}
              </Button>
            </FormGroup>

            <FormGroup>
              <Button
                bsStyle="link"
                disabled={busy}
                onClick={() => setAskClientId(false)}
                style={{ width: "100%" }}
              >
                Back
              </Button>
            </FormGroup>

            <div className="text-center" style={{ marginTop: 8 }}>
              <Link to="/" className="chat-link">
                Use username &amp; password instead
              </Link>
            </div>
          </form>
        )}

        <div id="footer" className="text-center" style={{ marginTop: 24 }}>
          <p>&copy; {new Date().getFullYear()} Foveonics Document Solutions</p>
        </div>
      </div>
    </div>
  );
}
